import { materialsService } from "./materials.service";
import { ocrImage, ocrPdf } from "./local-ocr.service";
import { localFiles } from "./document/localFiles";
import type { Material } from "@/types/models";

/**
 * Liest hochgeladene Materialien (PDF, Bilder, Textdateien) als Text aus,
 * damit Quiz- und Klausur-Generierung sie als Kontext nutzen können.
 * PDFs/Bilder laufen durch Tesseract.js (`local-ocr.service.ts`), komplett im
 * Browser, kein externer Anbieter und kein Schlüssel nötig. Das Ergebnis wird
 * pro Material in localStorage gecacht (Key enthält `updated`, d. h. eine
 * neue Dateiversion wird automatisch neu gelesen).
 */

const MAX_FILE_BYTES = 15_000_000; // größere Dateien überspringen (Base64-Limit)
const MAX_TEXT_PER_MATERIAL = 8_000;
const MAX_TEXT_TOTAL = 14_000;
const CACHE_PREFIX = "mocr_";

function cacheKey(m: Material): string {
  return `${CACHE_PREFIX}${m.id}_${m.updated}`;
}

function readCache(m: Material): string | null {
  try {
    return localStorage.getItem(cacheKey(m));
  } catch {
    return null;
  }
}

function writeCache(m: Material, text: string): void {
  try {
    // Alte Versionen desselben Materials aufräumen, dann neu schreiben.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(`${CACHE_PREFIX}${m.id}_`) && k !== cacheKey(m)) {
        localStorage.removeItem(k);
      }
    }
    localStorage.setItem(cacheKey(m), text);
  } catch {
    /* Quota voll – Cache ist nur Beschleunigung */
  }
}

/**
 * Text einer noch nicht hochgeladenen Datei (z. B. aus dem Sparkles-Popup) –
 * dieselbe Erkennung wie `materialText`, nur ohne PocketBase-Material und
 * ohne Cache (die Datei existiert ja nur für diesen einen Aufruf).
 */
export async function extractFileText(file: File): Promise<string> {
  const type = (file.type || "").toLowerCase();
  const isPdf = type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
  const isImage = type.startsWith("image/");
  const isText = type.startsWith("text/") || /\.(txt|md|csv)$/i.test(file.name);

  try {
    if (isText) return (await file.text()).slice(0, MAX_TEXT_PER_MATERIAL);
    if (isPdf && file.size <= MAX_FILE_BYTES) {
      return (await ocrPdf(await file.arrayBuffer())).slice(0, MAX_TEXT_PER_MATERIAL);
    }
    if (isImage && file.size <= MAX_FILE_BYTES) {
      return (await ocrImage(file)).slice(0, MAX_TEXT_PER_MATERIAL);
    }
  } catch {
    return "";
  }
  return "";
}

/** Text eines einzelnen Materials (gecacht). Leerstring, wenn nicht lesbar. */
export async function materialText(m: Material): Promise<string> {
  const cached = readCache(m);
  if (cached !== null) return cached;

  const type = (m.type ?? "").toLowerCase();
  const isPdf = type === "application/pdf" || m.file.toLowerCase().endsWith(".pdf");
  const isImage = type.startsWith("image/");
  const isText = type.startsWith("text/") || /\.(txt|md|csv)$/i.test(m.file);

  // Vom Gerät, falls dort (localFiles.ts) – sonst vom Server.
  const blobOf = async (): Promise<Blob> =>
    (await localFiles.get(m.id).catch(() => null))?.blob ??
    (await fetch(materialsService.urlFor(m)).then((r) => r.blob()));

  let text = "";
  let extracted = false; // nur erfolgreiche Läufe cachen (sonst bleibt z. B.
  try {                  // ein OCR-Fehler dauerhaft hängen)
    if (isText) {
      text = await (await blobOf()).text();
      extracted = true;
    } else if (isPdf) {
      const buf = await (await blobOf()).arrayBuffer();
      if (buf.byteLength <= MAX_FILE_BYTES) {
        text = await ocrPdf(buf);
        extracted = true;
      }
    } else if (isImage) {
      const blob = await blobOf();
      if (blob.size <= MAX_FILE_BYTES) {
        text = await ocrImage(blob);
        extracted = true;
      }
    } else {
      extracted = true; // nicht lesbarer Typ (Audio/Video): leeres Ergebnis ist final
    }
  } catch {
    text = "";
  }

  text = text.slice(0, MAX_TEXT_PER_MATERIAL);
  if (extracted) writeCache(m, text);
  return text;
}

/**
 * Gesamter Materialtext eines Themas (alle lesbaren Materialien,
 * mit Titel-Überschriften, gedeckelt). Leerstring wenn nichts lesbar.
 */
export async function topicMaterialsText(topicId: string): Promise<string> {
  const materials = await materialsService.listByTopic(topicId).catch(() => [] as Material[]);
  const parts: string[] = [];
  let total = 0;
  for (const m of materials) {
    if (total >= MAX_TEXT_TOTAL) break;
    const text = await materialText(m).catch(() => "");
    if (!text) continue;
    const chunk = `### Material: ${m.title}\n${text}`;
    parts.push(chunk.slice(0, MAX_TEXT_TOTAL - total));
    total += chunk.length;
  }
  return parts.join("\n\n");
}
